/**
 * DashboardNavbar
 * Top navigation for the dashboard with URL-based routing
 * Following the SettingsNavbar pattern
 */

import { useEffect, useContext, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Home, ListTodo, GraduationCap, Calendar, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SidebarContext } from '@/contexts/SidebarContext';
import { useLayoutState } from '@/hooks/useLayoutState';
import { useSetMobileNavHeight } from '@/hooks/useSetMobileNavHeight';

/**
 * 4-tab flat navigation structure:
 * 1. Dashboard (Home) - Overview widgets, stats, "Focus Today"
 * 2. Tasks - Task management
 * 3. Classes - Your classes + syllabus management (dev only)
 * 4. Plan - Study plan schedule (dev only)
 */
const allDashboardRoutes = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    shortLabel: 'Home',
    icon: Home,
    exact: true,
    devOnly: false,
  },
  {
    path: '/dashboard/tasks',
    label: 'Tasks',
    shortLabel: 'Tasks',
    icon: ListTodo,
    exact: false,
    devOnly: false,
  },
  {
    path: '/dashboard/classes',
    label: 'Classes',
    shortLabel: 'Classes',
    icon: GraduationCap,
    exact: false,
    devOnly: true, // Hide in production - feature in development
  },
  {
    path: '/dashboard/plan',
    label: 'Plan',
    shortLabel: 'Plan',
    icon: Calendar,
    exact: false,
    devOnly: true, // Hide in production - feature in development
  },
];

// Filter routes based on environment - Classes and Plan are dev-only for now
const isDev = import.meta.env.DEV || window.location.hostname === 'dev.jdsimplified.com';
const dashboardRoutes = allDashboardRoutes.filter(route => !route.devOnly || isDev);

export function DashboardNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isExpanded, setIsExpanded } = useContext(SidebarContext);
  const { isDesktop, isPinned, contentMargin } = useLayoutState();

  // Get current page title for mobile header
  const getCurrentTitle = () => {
    const route = dashboardRoutes.find((r) => {
      if (r.exact) {
        return location.pathname === r.path;
      }
      return location.pathname.startsWith(r.path);
    });
    return route?.label || 'Dashboard';
  };

  // Check if route is active
  const isRouteActive = (route: (typeof dashboardRoutes)[0]) => {
    if (route.exact) {
      return location.pathname === route.path;
    }
    return location.pathname.startsWith(route.path);
  };

  // Set document title based on current route
  useEffect(() => {
    const title = getCurrentTitle();
    document.title = `${title} - JD Simplified`;
  }, [location]);

  const NavLink = ({
    to,
    icon: Icon,
    text,
    isActive,
  }: {
    to: string;
    icon: typeof Home;
    text: string;
    isActive: boolean;
  }) => (
    <Link
      to={to}
      className={cn(
        'flex items-center space-x-1 py-1.5 px-2 rounded-md text-base transition-colors',
        isActive
          ? 'text-[#F37022]'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
      )}
    >
      <Icon className="h-5 w-5" />
      <span>{text}</span>
    </Link>
  );

  return (
    <>
      <nav
        className={cn(
          'glass dark:glass-dark sticky top-0 z-30 w-full transition-all duration-300 ease-in-out',
          isDesktop && isPinned && !isExpanded && contentMargin
        )}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Mobile header */}
            <div className="md:hidden flex items-center justify-between w-full">
              {/* Hamburger menu button */}
              <button
                onClick={() => setIsExpanded(true)}
                className="bg-[#F37022] text-white flex items-center justify-center p-2 rounded-md hover:bg-[#E36012]"
                aria-label="Open sidebar"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="flex flex-col flex-grow">
                <h1 className="text-lg font-semibold text-center dark:text-white">
                  {getCurrentTitle()}
                </h1>
              </div>

              {/* New Task button (mobile) */}
              <Button
                size="sm"
                onClick={() => navigate('/dashboard/tasks/create')}
              >
                <Plus className="h-4 w-4 mr-1" />
                <span className="sr-only sm:not-sr-only">New</span>
              </Button>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:grid md:grid-cols-[auto_1fr_auto] md:items-center md:w-full md:gap-4">
              {/* Navigation links */}
              <div className="md:flex md:items-center md:space-x-2 lg:space-x-4 xl:space-x-5">
                {dashboardRoutes.map((route) => (
                  <NavLink
                    key={route.path}
                    to={route.path}
                    icon={route.icon}
                    text={route.label}
                    isActive={isRouteActive(route)}
                  />
                ))}
              </div>

              {/* Center section - empty for now */}
              <div className="flex-1 flex justify-center" />

              {/* Right actions - New Task button (always visible) */}
              <div className="flex items-center space-x-4">
                <Button onClick={() => navigate('/dashboard/tasks/create')}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Task
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile bottom navigation */}
      <DashboardMobileNav />
    </>
  );
}

/**
 * Mobile bottom navigation for dashboard
 */
function DashboardMobileNav() {
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  useSetMobileNavHeight(navRef);

  const isRouteActive = (route: (typeof dashboardRoutes)[0]) => {
    if (route.exact) {
      return location.pathname === route.path;
    }
    return location.pathname.startsWith(route.path);
  };

  return (
    <nav
      ref={navRef}
      className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50 safe-area-bottom"
    >
      <div className="grid grid-cols-4 h-16">
        {dashboardRoutes.map((route) => {
          const isActive = isRouteActive(route);
          const Icon = route.icon;

          return (
            <Link
              key={route.path}
              to={route.path}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 transition-colors',
                isActive
                  ? 'text-[#F37022]'
                  : 'text-gray-500 dark:text-gray-400'
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs font-medium">{route.shortLabel}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default DashboardNavbar;
